/**
 * AETERNA HTTP 客户端
 *
 * 基于 fetch 的请求封装：
 * - 自动注入 Authorization header
 * - 统一错误码映射
 * - 401 时触发 token refresh
 * - 幂等请求自动重试
 */

import { API_BASE } from './config';
import { getStoredLocale } from '../i18n/locale';

// ── 错误类型 ──

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public retryable: boolean = false,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Token 管理（由 session-store 注入） ──

type TokenGetter = () => string | null;
type TokenRefresher = () => Promise<boolean>;
type LogoutHandler = () => void;

let _getToken: TokenGetter = () => null;
let _refreshToken: TokenRefresher = async () => false;
let _onLogout: LogoutHandler = () => {};
let _refreshPromise: Promise<boolean> | null = null;

/** 由 session-store 在初始化时调用 */
export function setAuthHandlers(
  getToken: TokenGetter,
  refreshToken: TokenRefresher,
  onLogout: LogoutHandler,
) {
  _getToken = getToken;
  _refreshToken = refreshToken;
  _onLogout = onLogout;
}

/** SSE 等外部模块使用 — 获取当前 auth 函数 */
export const getAuthFns = () => ({
  getToken: _getToken,
  refreshToken: _refreshToken,
  onLogout: _onLogout,
});

// ── 核心请求 ──

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** 跳过 auth header（用于登录接口） */
  skipAuth?: boolean;
  /** 跳过自动重试 */
  skipRetry?: boolean;
  /** 跳过 401 拦截（防止刷新 token 时死循环） */
  skip401?: boolean;
}

async function rawFetch(path: string, options: RequestOptions = {}): Promise<Response> {
  const { body, skipAuth, skipRetry, ...init } = options;
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  const headers = new Headers(init.headers);
  if (body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (!skipAuth) {
    const token = _getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  if (!headers.has('Accept-Language')) {
    headers.set('Accept-Language', getStoredLocale());
  }

  const response = await fetch(url, {
    ...init,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  return response;
}

/** 判断是否为幂等方法（可安全重试） */
function isIdempotent(method?: string): boolean {
  const m = (method ?? 'GET').toUpperCase();
  return m === 'GET' || m === 'PUT' || m === 'DELETE';
}

/**
 * 发起 API 请求，返回解析后的 JSON
 *
 * 后端统一响应格式: { success: boolean, data?: T, error?: { code, message } }
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response: Response;

  try {
    response = await rawFetch(path, options);
  } catch (err) {
    // 网络错误：幂等请求重试一次
    if (!options.skipRetry && isIdempotent(options.method)) {
      try {
        response = await rawFetch(path, { ...options, skipRetry: true });
      } catch {
        throw new ApiError('NETWORK_ERROR', '网络不可用，请检查连接', 0, true);
      }
    } else {
      throw new ApiError('NETWORK_ERROR', '网络不可用，请检查连接', 0, true);
    }
  }

  // 401: singleflight refresh — 并发请求共享同一个 refresh Promise
  if (response.status === 401 && !options.skipAuth && !options.skip401) {
    if (!_refreshPromise) {
      _refreshPromise = _refreshToken().finally(() => { _refreshPromise = null; });
    }
    const refreshed = await _refreshPromise;
    if (refreshed) {
      return request(path, { ...options, skipRetry: true, skip401: true });
    }
    _onLogout();
    throw new ApiError('UNAUTHORIZED', '登录已过期，请重新登录', 401);
  }

  // 解析响应
  const json = await response.json().catch(() => null);

  if (!response.ok) {
    const message = json?.error?.message ?? json?.message ?? `请求没有完成 (${response.status})`;
    const code = json?.error?.code ?? `HTTP_${response.status}`;
    const retryable = response.status >= 500;
    throw new ApiError(code, message, response.status, retryable);
  }

  // 后端 ApiResponse 格式
  if (json && typeof json === 'object' && 'data' in json) {
    return json.data as T;
  }

  return json as T;
}

/** SSE / streaming POST — returns raw Response so caller can read body as stream */
export async function streamPost(path: string, body: unknown): Promise<Response> {
  return rawFetch(path, { method: 'POST', body });
}

// ── 便捷方法 ──

export const http = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
